import {
  MatchKeyMatchableType,
  MaterialState,
  ModelError_Item,
  patchV1CompaniesCompanyIdMaterialsId,
  postV1CompaniesCompanyIdMaterials,
} from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import MatchKeysSection from '~components/MatchKeys/MatchKeysSection';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Material } from '~hooks/useMaterials/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import MaterialForm, { MaterialFormHandler } from './MaterialForm';
import { MaterialDto, MaterialDtoAsyncError } from './materialSchema';

interface MaterialDialogProps {
  isOpen: boolean;
  material?: Material;
  onClose: () => void;
  onSuccess?: () => void;
}

const MaterialDialog = observer(
  ({ isOpen, material, onClose, onSuccess }: MaterialDialogProps) => {
    const { companyAssetsStore, toasterStore, userStore } = useStores();
    const materialFormRef = useRef<MaterialFormHandler>(null);
    const [asyncErrors, setAsyncErrors] = useState<MaterialDtoAsyncError[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const companyId = getEffectiveUserCompanyId(userStore);

    const closeDialog = () => {
      setAsyncErrors([]);
      onClose();
    };

    const handleClose = () => {
      if (isSubmitting) {
        return;
      }

      closeDialog();
    };

    const handleSubmit = async () => {
      const formData = await materialFormRef.current?.onSubmit();

      if (!formData) {
        return;
      }

      const materialId = material?.id;

      try {
        setAsyncErrors([]);
        setIsSubmitting(true);

        if (materialId) {
          const response = await patchV1CompaniesCompanyIdMaterialsId({
            path: { 'company-id': companyId, id: materialId },
            body: {
              name: formData.name,
              material_type_id: formData.type?.value,
              external_id: formData.externalId,
              state: formData.isNotActive ? MaterialState.INACTIVE : MaterialState.ACTIVE,
            },
          });
          const parsed = Material.parse(response.data.data);
          const name = parsed.name;

          companyAssetsStore.updateMaterial(parsed);
          toasterStore.push(
            alert(
              t('administration.material.material_updated', { name }),
              AlertTypes.success,
            ),
          );
        } else {
          const response = await postV1CompaniesCompanyIdMaterials({
            path: { 'company-id': formData.company.value },
            body: {
              name: formData.name,
              material_type_id: formData.type?.value,
              external_id: formData.externalId,
              state: formData.isNotActive ? MaterialState.INACTIVE : MaterialState.ACTIVE,
            },
          });
          const parsed = Material.parse(response.data.data);
          const name = parsed.name;

          companyAssetsStore.addMaterial(parsed);
          toasterStore.push(
            alert(
              t('administration.material.material_created', { name }),
              AlertTypes.success,
            ),
          );
        }

        setIsSubmitting(false);
        onSuccess?.();
        closeDialog();
      } catch (error) {
        setIsSubmitting(false);

        if (axios.isAxiosError(error)) {
          const errorsArray = (error.response?.data?.error?.errors ??
            []) as ModelError_Item[];

          const nextAsyncErrors: MaterialDtoAsyncError[] = [];
          const formFieldInfoByFieldInError: Record<
            string,
            { fieldInForm: keyof MaterialDto; label: string }
          > = {
            name: { fieldInForm: 'name', label: t('form_fields.name') },
          };

          errorsArray.forEach((errorsArrayItem) => {
            const { field, message } = errorsArrayItem;

            const isValueTakenError = message.includes('has already been taken');
            const fieldInfo = formFieldInfoByFieldInError[field];

            if (isValueTakenError && fieldInfo) {
              nextAsyncErrors.push({
                field: fieldInfo.fieldInForm,
                message: t('error_messages.field_taken', { field: fieldInfo.label }),
              });
            }
          });

          if (nextAsyncErrors.length) {
            setAsyncErrors(nextAsyncErrors);
          } else {
            connection.handleRequestError(
              error,
              materialId
                ? t('error_messages.materials.failed_to_update')
                : t('error_messages.materials.failed_to_create'),
            );
          }
        } else {
          connection.handleRequestError(
            error,
            materialId
              ? t('error_messages.materials.failed_to_update')
              : t('error_messages.materials.failed_to_create'),
          );
        }
      }
    };

    return (
      <Dialog isOpen={isOpen} preventCloseOnBackdropClick preventCloseOnEscapeKeyDown>
        <DialogHeader onClose={handleClose}>
          {material
            ? t('administration.material.update_material')
            : t('administration.material.create_material')}
        </DialogHeader>

        <DialogContent>
          <MaterialForm
            ref={materialFormRef}
            asyncErrors={asyncErrors}
            material={material}
          />

          <MatchKeysSection
            companyId={companyId}
            matchableId={material?.id}
            matchableType={MatchKeyMatchableType.MATERIAL}
            recordName={material?.name ?? ''}
          />
        </DialogContent>

        <DialogActions>
          {Boolean(asyncErrors.length) && (
            <ShakyErrorIndicator
              message={t('administration.users.user_form.invalid_data_error_messages')}
            />
          )}

          <SmallButton
            color="secondary"
            disabled={isSubmitting}
            onClick={handleClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton loading={isSubmitting} onClick={handleSubmit}>
            {material ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    );
  },
);

export default MaterialDialog;
